import { Injectable } from '@angular/core';
import { ActionPerformed, PushNotificationSchema, PushNotifications, Token } from '@capacitor/push-notifications';
import gql from 'graphql-tag';
import { AuthenticationService } from '../authentication/authentication.service';
import { ConfigService } from '../config/config.service';
import { GraphqlService } from '../http/graphql.service';
import { LogService } from '../log/log.service';
import { NativeStorageService } from '../storage/native-storage.service';
import { PoolService } from '../pool/pool.service';
import { UserService } from '../user/user.service';
import { DeviceSettingsService } from '../device-settings/device-settings.service';

@Injectable({ providedIn: 'root' })
export class PushNotificationService {

  private subscribeToClientNotificationsMutation = gql`
    mutation($deviceId: String!, $secret: String!, $clientId: ID!) {
      updateDeviceSettings(
        data: { notificationClients: { connect: [{ id: $clientId }] } }
        where: { deviceId: $deviceId }
        secret: $secret
      ) {
        id
      }
    }
  `;

  private inheritDeviceNotificationsMutation = gql`
    mutation($deviceId: String!, $secret: String!, $data: DeviceSettingsUpdateInput!) {
      updateDeviceSettings(data: $data, where: { deviceId: $deviceId }, secret: $secret) {
        id
      }
      inheritDeviceNotifications(deviceId: $deviceId, secret: $secret)
    }
  `;

  private updateDeviceSettingsRegistrationTokenMutation = gql`
    mutation($deviceId: String!, $secret: String!, $registrationToken: String!) {
      updateDeviceSettings(
        data: { registrationToken: $registrationToken }
        where: { deviceId: $deviceId }
        secret: $secret
      ) {
        id
      }
    }
  `;

  private listenersRegistered = false;
  private cachedToken?: string;
  private initializationInFlight?: Promise<boolean>;

  constructor(
    private logService: LogService,
    private userService: UserService,
    private authService: AuthenticationService,
    private nativeStorage: NativeStorageService,
    private poolService: PoolService,
    private configService: ConfigService,
    private graphqlService: GraphqlService,
    private deviceSettingsService: DeviceSettingsService,
  ) { }

  public init(): Promise<boolean> {
    return this.initializationInFlight ??= this.initialize()
      .finally(() => this.initializationInFlight = undefined);
  }

  private async initialize(): Promise<boolean> {
    try {
      if (!this.configService.isMobileApp) {
        return false;
      }
      this.cachedToken = await this.loadRegistrationToken();
      await this.registerListeners();
      void this.updateRegistrationToken().catch(error => this.logService.logError(error));

      let permissionStatus = await PushNotifications.checkPermissions();
      if (permissionStatus.receive === 'prompt' || permissionStatus.receive === 'prompt-with-rationale') {
        permissionStatus = await PushNotifications.requestPermissions();
      }

      if (permissionStatus.receive !== 'granted') {
        this.logService.logError('Push notification permission not granted');
        return false;
      }

      await PushNotifications.register();
      return true;
    } catch (error) {
      this.logService.logError(error);
      return false;
    }
  }

  public async subscribeToClientNotifications(clientId: string) {
    const credentials = await this.deviceSettingsService.getCredentials();
    await this.graphqlService.mutate(this.subscribeToClientNotificationsMutation, { ...credentials, clientId });
    await this.updateRegistrationToken();
  }

  public async updatePoolsRegistrationToken(registrationToken) {
    const pools = await this.nativeStorage.getItem('pools').catch(() => []);
    return pools.reduce((promise, pool) => promise.then(
      () => this.poolService.updatePoolRegistrationToken(
        pool.id,
        registrationToken,
      ),
    ), Promise.resolve());
  }

  public updateUserRegistrationToken(userId, registrationToken) {
    return this.userService.updateUser({ registrationToken }, { id: userId });
  }

  public async updateDeviceSettingsRegistrationToken(registrationToken) {
    const { deviceId, secret } = await this.deviceSettingsService.getCredentials();
    return this.graphqlService.mutate(this.updateDeviceSettingsRegistrationTokenMutation, {
      deviceId,
      secret,
      registrationToken,
    });
  }

  public async updateRegistrationToken() {
    const registrationToken = await this.loadRegistrationToken();
    const userId = this.authService.getUserId();
    if (userId && !this.configService.isProApp) {
      const credentials = await this.deviceSettingsService.getCredentials();
      return this.graphqlService.mutate(this.inheritDeviceNotificationsMutation, {
        ...credentials,
        data: registrationToken ? { registrationToken } : {},
      });
    }
    if (!registrationToken) {
      return;
    }
    if (userId) {
      await this.updateUserRegistrationToken(userId, registrationToken);
    } else {
      await Promise.all([
        this.updatePoolsRegistrationToken(registrationToken),
        this.updateDeviceSettingsRegistrationToken(registrationToken),
      ]);
    }
  }

  public onMessageReceived(callback) {
    if (!this.configService.isMobileApp) {
      return;
    }

    PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
      callback({ ...notification, ...notification.data });
    });
    PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
      callback({ ...action.notification, ...action.notification.data, tap: true });
    });
  }

  public loadRegistrationToken() {
    return this.nativeStorage.getItem('registrationToken').catch(() => undefined);
  }

  private saveRegistrationToken(token) {
    return this.nativeStorage.setItem('registrationToken', token);
  }

  private async registerListeners() {
    if (this.listenersRegistered) {
      return;
    }
    await PushNotifications.addListener('registration', (token: Token) => {
      void this.saveRegistrationToken(token.value).then(async () => {
        if (token.value !== this.cachedToken) {
          this.cachedToken = token.value;
          await this.updateRegistrationToken();
        }
      }).catch(error => this.logService.logError(error));
    });

    await PushNotifications.addListener('registrationError', (error) => this.logService.logError(error));
    this.listenersRegistered = true;
  }

}
